import { useState, useEffect, useMemo, createContext, useContext, type HTMLAttributes } from 'react';
import {
  endOfMonth,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  format,
} from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { Button } from './ui/Button';
import { Calendar } from './ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/Popover';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/Tooltip';
import { useDateRange } from '../context/DateRangeContext';
import { useCalendarQuality, type DayQuality } from '../hooks/use-calendar-quality';
import type { DateRange as DayPickerRange, CalendarDay, Modifiers } from 'react-day-picker';

export interface DatePickerRangeValue {
  startDate: Date;
  endDate: Date;
}

export interface DatePickerWithRangeProps {
  value?: DatePickerRangeValue;
  onChange?: (range: DatePickerRangeValue) => void;
  showQuality?: boolean;
}

// ─── Quality context (passes quality map into custom Day component) ─────────

const QualityMapContext = createContext<Map<string, DayQuality>>(new Map());

// ─── Platform display labels ────────────────────────────────────────────────

const PLATFORM_LABELS: Record<string, string> = {
  shopify: 'Shopify',
  amazon_seller: 'Amazon',
  amazon_ads: 'Amazon Ads',
  meta: 'Meta',
  google_ads: 'Google Ads',
  tiktok_shop: 'TikTok Shop',
  tiktok_business: 'TikTok Ads',
  pinterest: 'Pinterest',
  klaviyo: 'Klaviyo',
};

const STATUS_DOT: Record<string, string> = {
  full: 'bg-green-500',
  unknown: 'bg-green-500',
  partial: 'bg-amber-500',
  stale: 'bg-amber-500',
  missing: 'bg-red-400',
};

// ─── Custom Day component (dot indicator + hover card) ──────────────────────

function QualityDay({ day, modifiers, children, ...rest }: {
  day: CalendarDay;
  modifiers: Modifiers;
} & HTMLAttributes<HTMLDivElement>) {
  const qualityMap = useContext(QualityMapContext);
  const quality = qualityMap.get(day.isoDate);

  // Dot color based on Shopify (primary platform), fallback to overall
  let dotClass = '';
  if (quality) {
    const shopify = quality.platforms['shopify'];
    const primaryStatus = shopify ? shopify.status : quality.status;
    dotClass = STATUS_DOT[primaryStatus] ?? '';
  }

  const platforms = quality ? Object.entries(quality.platforms) : [];

  const inner = (
    <div {...rest} className={cn(rest.className, 'relative')}>
      {children}
      {dotClass && (
        <span className={cn(
          'absolute bottom-0.5 left-1/2 -translate-x-1/2 size-[5px] rounded-full pointer-events-none',
          dotClass,
        )} />
      )}
    </div>
  );

  if (platforms.length === 0) return inner;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{inner}</TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="center"
        className="p-0 bg-white dark:bg-grey-800 border border-grey-50 dark:border-grey-600 shadow-lg rounded-lg"
      >
        <div className="px-3 py-2 min-w-[160px]">
          <p className="text-[10px] font-semibold text-charcoalGray-400 dark:text-charcoalGray-300 uppercase tracking-wider mb-1.5">
            {day.isoDate}
          </p>
          <div className="space-y-1">
            {platforms.map(([key, p]) => (
              <div key={key} className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-1.5">
                  <span className={cn('size-[6px] rounded-full shrink-0', STATUS_DOT[p.status] ?? 'bg-gray-300')} />
                  <span className="text-[11px] text-charcoalGray-700 dark:text-charcoalGray-200">
                    {PLATFORM_LABELS[key] ?? key}
                  </span>
                </div>
                {p.rowCount > 0 && (
                  <span className="text-[10px] text-charcoalGray-400 dark:text-charcoalGray-300 tabular-nums">
                    {p.rowCount.toLocaleString()}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function DatePickerWithRange({ value, onChange, showQuality = true }: DatePickerWithRangeProps = {}) {
  const { dateRange: contextDateRange, setDateRange: setContextDateRange, timeZone, zonedNow } = useDateRange();
  const dateRange = value ?? contextDateRange;
  const setDateRange = onChange ?? setContextDateRange;
  const internalDateRange: DayPickerRange = {
    from: dateRange.startDate,
    to: dateRange.endDate,
  };

  const [tempDate, setTempDate] = useState<DayPickerRange | undefined>(internalDateRange);
  const [isOpen, setIsOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(
    () => startOfMonth(subMonths(dateRange.startDate, 1)),
  );

  // Track viewport size so the calendar shows 1 month on mobile, 2 on desktop.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Fetch data quality for the two calendar months currently visible (skip in controlled non-store contexts)
  const { qualityMap, fullDates, partialDates, missingDates } = useCalendarQuality(visibleMonth, { enabled: showQuality });

  // react-day-picker modifiers: classify days by quality status
  const qualityModifiers = useMemo(() => ({
    qualityFull: fullDates,
    qualityPartial: partialDates,
    qualityMissing: missingDates,
  }), [fullDates, partialDates, missingDates]);

  // Stable components object (memoized) — custom Day with quality dot only when enabled
  const calendarComponents = useMemo(
    () => (showQuality ? { Day: QualityDay } : undefined),
    [showQuality],
  );

  const handleApply = () => {
    if (tempDate?.from && tempDate?.to) {
      setDateRange({ startDate: tempDate.from, endDate: tempDate.to });
      setIsOpen(false);
    } else {
      toast.warning('Please select a date range.');
    }
  };

  useEffect(() => {
    setTempDate(internalDateRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.startDate, dateRange.endDate]);

  const handleCancel = () => {
    setTempDate(internalDateRange);
    setIsOpen(false);
  };

  const quickSelectOptions = [
    {
      label: 'Today',
      range: { from: zonedNow, to: zonedNow },
    },
    {
      label: 'Yesterday',
      range: { from: subDays(zonedNow, 1), to: subDays(zonedNow, 1) },
    },
    {
      label: 'Last 7 days',
      range: { from: subDays(zonedNow, 7), to: subDays(zonedNow, 1) },
    },
    {
      label: 'Last 30 days',
      range: { from: subDays(zonedNow, 30), to: subDays(zonedNow, 1) },
    },
    {
      label: 'Last 90 days',
      range: { from: subDays(zonedNow, 90), to: subDays(zonedNow, 1) },
    },
    {
      label: 'Last Month',
      range: {
        from: startOfMonth(subMonths(zonedNow, 1)),
        to: endOfMonth(subMonths(zonedNow, 1)),
      },
    },
    {
      label: 'This Week',
      range: {
        from: startOfWeek(zonedNow, { weekStartsOn: 1 }),
        to: zonedNow,
      },
    },
    {
      label: 'This Month',
      range: { from: startOfMonth(zonedNow), to: zonedNow },
    },
    {
      label: 'This Quarter',
      range: { from: startOfQuarter(zonedNow), to: zonedNow },
    },
    {
      label: 'This Year',
      range: { from: startOfYear(zonedNow), to: zonedNow },
    },
  ];

  const handleQuickSelect = (range: { from: Date; to: Date }) => {
    setTempDate(range);
    setDateRange({ startDate: range.from, endDate: range.to });
    setIsOpen(false);
  };

  const getGMTOffset = () => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
      });
      const parts = formatter.formatToParts(zonedNow);
      const offset = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
      return `(${offset})`;
    } catch {
      return '';
    }
  };

  return (
    <div className="grid gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'w-full p-0 justify-between rounded-md font-normal dark:text-white bg-white text-grey-900 dark:bg-grey-800 border border-grey-50 dark:border-grey-700 overflow-hidden',
              !internalDateRange && 'text-grey-100 dark:text-grey-500',
            )}
          >
            <span className="min-w-0 flex-1 truncate py-3 pl-3 pr-2 text-left text-xs sm:text-sm">
              {internalDateRange?.from ? (
                internalDateRange.to ? (
                  <>
                    {format(internalDateRange.from, 'LLL dd, y')} –{' '}
                    {format(internalDateRange.to, 'LLL dd, y')}
                  </>
                ) : (
                  format(internalDateRange.from, 'LLL dd, y')
                )
              ) : (
                <span>Pick a date</span>
              )}
            </span>
            <span className="shrink-0 self-stretch bg-primary-500 p-2 flex items-center justify-center rounded-r-md">
              <CalendarIcon className="text-white size-4 sm:size-5" />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto max-w-[calc(100vw-1rem)] p-0 dark:border-grey-500"
          align={isMobile ? 'center' : 'end'}
          collisionPadding={8}
        >
          <div className="flex flex-col md:flex-row">
            {/* Quick select — horizontal scroll on mobile, vertical sidebar on desktop */}
            <div
              className="flex flex-row gap-1 overflow-x-auto border-b border-grey-50 px-2 py-1 dark:border-grey-500 bg-white dark:bg-grey-900 rounded-t-md md:flex-col md:gap-0 md:overflow-visible md:border-b-0 md:border-r md:px-0 md:pl-1 md:pr-5 md:pt-1 md:rounded-t-none md:rounded-tl-md"
            >
              {quickSelectOptions.map(option => (
                <Button
                  key={option.label}
                  variant="ghost"
                  onClick={() => handleQuickSelect(option.range)}
                  className="shrink-0 whitespace-nowrap justify-start border-none bg-transparent px-2 text-left text-primary-500 shadow-none transition-all duration-300 hover:bg-grey-50 dark:hover:bg-grey-700 md:mb-1"
                >
                  {option.label}
                </Button>
              ))}
            </div>
            {/* Calendar */}
            <QualityMapContext.Provider value={qualityMap}>
              <Calendar
                autoFocus
                mode="range"
                defaultMonth={internalDateRange.from}
                selected={tempDate}
                onSelect={setTempDate}
                onMonthChange={setVisibleMonth}
                numberOfMonths={isMobile ? 1 : 2}
                className="flex flex-col bg-white dark:bg-grey-900 dark:text-white md:rounded-tr-md"
                disabled={{ after: zonedNow }}
                today={zonedNow}
                modifiers={qualityModifiers}
                components={calendarComponents}
              />
            </QualityMapContext.Provider>
          </div>
          {/* Footer */}
          <div className="flex flex-col gap-2 p-2 border-t border-grey-50 dark:border-grey-500 bg-white dark:bg-grey-900 dark:text-white rounded-b-md md:flex-row md:items-center md:justify-between">
            <p className="pl-1 text-xs md:text-sm">
              <b>Time Zone:</b> {timeZone} {getGMTOffset()}
            </p>
            <div className="flex justify-end gap-2 items-center">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="dark:hover:bg-white dark:hover:text-grey-900 dark:hover:border-white hover:border-grey-50 hover:bg-grey-50"
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleApply}
                disabled={!tempDate?.from || !tempDate?.to}
                className="bg-primary-500 text-white outline-none hover:bg-primary-600 focus:outline-none dark:bg-primary-500 dark:hover:bg-primary-600"
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
